/**
 * Collection grid — measurements for the sticky sidebar and sticky toolbar.
 *
 * The sticking itself is pure CSS (assets/collection-sticky-filters.css);
 * sticky boundaries come from the DOM structure, so nothing here scrolls,
 * positions or pins anything. This script only publishes two numbers that
 * CSS cannot measure on its own, plus one state class:
 *
 *   --collection-sticky-header   height of the site header WHILE it is
 *                                position:fixed. The theme's
 *                                assets/header-sticky.js adds `.sticky` to
 *                                .header__sticky on scroll; until then the
 *                                header is in normal flow and occupies no
 *                                viewport top, so the value is 0.
 *   --collection-toolbar-height  height of the toolbar row, so the sidebar
 *                                parks below it rather than underneath it.
 *   .is-stuck                    on the toolbar wrapper, only while it is
 *                                actually detached — CSS has no selector for
 *                                "this sticky element is currently stuck".
 *
 * Both variables carry 0px fallbacks in the stylesheet, so if this file
 * fails to load the layout degrades to a plain sticky offset instead of
 * breaking.
 *
 * NOT AFFECTED BY FILTERING OR PAGINATION: the toolbar and the sidebar both
 * live OUTSIDE #ProductGridContainer, the only subtree assets/facets.js
 * replaces, so neither element is ever destroyed mid-session and there is
 * nothing to re-arm. Appended products change the grid's height, which moves
 * the sticky release point on its own — that is CSS, not this file.
 *
 * See Blueprint/shop.md.
 */
(function () {
  "use strict";

  var DESKTOP_QUERY = "(min-width: 750px)";

  var section = document.querySelector(".collection-product-grid-page");
  if (!section) return;

  var toolbar = section.querySelector(".collection-sticky-toolbar");
  var sentinel = section.querySelector(".collection-toolbar-sentinel");

  /* ALL of them, not the first. sections/header.liquid puts `header__sticky`
     on BOTH .header_bottom and .header__menu--bar, and the theme's
     header-sticky.js fixes every one it finds. Which of them is visible (and
     how tall it is) depends on the header type the merchant picked, so
     measuring only the first reports the wrong bar's height — that was the
     source of the leftover gap under the header. */
  var headers = document.querySelectorAll(".header__sticky");

  var lastHeaderHeight = null;
  var lastToolbarHeight = null;
  var frame = null;

  function setVar(name, value, previous) {
    if (value === previous) return previous;
    section.style.setProperty(name, value + "px");
    return value;
  }

  /* The lowest point any fixed header bar reaches in the viewport — which is
     precisely where the toolbar must dock.
     Measured as rect.bottom rather than rect.height on purpose: bottom is
     already viewport-relative, so it stays correct no matter what each bar's
     own offset is, and taking the maximum across the bars handles headers
     that stack more than one.
     The computed-position check matters: `.sticky` is added to every bar on
     scroll, but `.not--sticky-on-desktop` (and the max-width:749px rule in
     assets/header.css) leaves some of them in normal flow, where they
     occupy no viewport top at all and must not count. */
  function measureHeader() {
    var bottom = 0;

    for (var i = 0; i < headers.length; i++) {
      var el = headers[i];
      if (!el.classList.contains("sticky")) continue;
      if (window.getComputedStyle(el).position !== "fixed") continue;

      var rect = el.getBoundingClientRect();
      if (rect.height === 0) continue;
      if (rect.bottom > bottom) bottom = rect.bottom;
    }

    return Math.round(bottom);
  }

  function measureToolbar() {
    if (!toolbar) return 0;
    return Math.round(toolbar.getBoundingClientRect().height);
  }

  function measure() {
    frame = null;
    lastHeaderHeight = setVar("--collection-sticky-header", measureHeader(), lastHeaderHeight);
    lastToolbarHeight = setVar("--collection-toolbar-height", measureToolbar(), lastToolbarHeight);
  }

  function scheduleMeasure() {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(measure);
  }

  measure();

  window.addEventListener("scroll", scheduleMeasure, { passive: true });
  window.addEventListener("resize", scheduleMeasure, { passive: true });

  /* The toolbar's height changes with its own content — the product count
     text and the selected sort name both rewrite themselves after a filter
     change, and either can wrap to a second line. The header bars are
     watched too: their height can change between their normal and sticky
     states. */
  if (window.ResizeObserver) {
    var resizeObserver = new ResizeObserver(scheduleMeasure);
    if (toolbar) resizeObserver.observe(toolbar);
    for (var h = 0; h < headers.length; h++) resizeObserver.observe(headers[h]);
  }

  /* sections/header.liquid animates the header in with a 0.7s `smoothScroll`
     keyframe (translateY(-40px) → 0) plus an `all 0.3s` transition. A
     measurement taken mid-animation reports a bottom edge 40px too high, and
     if the shopper stops scrolling during it that stale value is the one the
     toolbar docks to. Re-measuring when the motion ends settles it. */
  for (var e = 0; e < headers.length; e++) {
    headers[e].addEventListener("animationend", scheduleMeasure);
    headers[e].addEventListener("transitionend", scheduleMeasure);
  }

  /* ------------------------------------------------------------------ *
   * Stuck detection
   *
   * The sentinel is a zero-height marker at the toolbar's resting position.
   * Shrinking the observer's viewport by the sticky offset from the top
   * means the sentinel leaves that box at precisely the scroll position
   * where the toolbar detaches — so `is-stuck` flips exactly in step with
   * the CSS, with no scroll-position arithmetic of our own.
   * ------------------------------------------------------------------ */
  if (toolbar && sentinel && "IntersectionObserver" in window) {
    var observer = null;

    var arm = function () {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      /* Below 750px nothing is sticky, so make sure a stale stuck state
         from a wider viewport cannot survive a resize. */
      if (!window.matchMedia(DESKTOP_QUERY).matches) {
        toolbar.classList.remove("is-stuck");
        return;
      }

      var styles = window.getComputedStyle(toolbar);
      var offset = parseFloat(styles.top) || 0;

      observer = new IntersectionObserver(
        function (entries) {
          toolbar.classList.toggle("is-stuck", !entries[0].isIntersecting);
        },
        { rootMargin: "-" + (offset + 1) + "px 0px 0px 0px", threshold: 0 }
      );
      observer.observe(sentinel);
    };

    arm();
    /* The offset baked into rootMargin depends on the header height and the
       breakpoint, so the observer is rebuilt whenever either can change. */
    window.addEventListener("resize", arm, { passive: true });
    for (var i = 0; i < headers.length; i++) {
      new MutationObserver(arm).observe(headers[i], {
        attributes: true,
        attributeFilter: ["class"],
      });
    }
  }
})();
